import React, { useEffect, useState } from "react";
import { Platform } from "react-native";
import { useSelector } from "react-redux";
import {
  checkMultiple,
  RESULTS,
  type Permission,
  type PermissionStatus,
} from "react-native-permissions";
import MovexGeo from "@movexgeo/react-native-movex";
import { trackingEnabled } from "@config";
import { loadRequiredPermissionKeys } from "./appPermissions";

const isGranted = (status?: PermissionStatus) =>
  status === RESULTS.GRANTED || status === RESULTS.LIMITED;

/**
 * Mounted only after login. Sets the CP DB user id on the (already
 * initialised at app boot) movex.geo SDK and starts adaptive tracking.
 *
 * PermissionsGate (mounted always) handles permission collection — if
 * anything is missing here we skip start; the gate's modal will surface
 * to collect them.
 */
const TrackingBootstrap: React.FC = () => {
  const user = useSelector(
    (s: {
      auth: { user?: { id?: string; mobile?: string; cp_db_id?: string } };
    }) => s?.auth?.user,
  );
  const userId = user?.cp_db_id || user?.mobile || user?.id;
  const userToken = useSelector(
    (s: { auth: { userToken?: string } }) => s?.auth?.userToken,
  );

  const [permissionKeys, setPermissionKeys] = useState<Permission[] | null>(
    null,
  );

  useEffect(() => {
    if (Platform.OS !== "android") return;
    if (!trackingEnabled) return;
    let cancelled = false;
    (async () => {
      const keys = await loadRequiredPermissionKeys();
      if (cancelled) return;
      setPermissionKeys(keys);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (Platform.OS !== "android") return;
    if (!trackingEnabled) return;
    if (!permissionKeys) return;

    // Explicit logout / session gone → stop. Otherwise the FGS must
    // outlive this component: swipe-from-recents tears down the JS
    // context and unmounts every component, but the SDK's FGS is
    // designed to survive that via onTaskRemoved → AlarmManager
    // re-spawn. Calling stopTracking() from a useEffect cleanup would
    // clear the persisted preset and tell the SDK "the host wants to
    // stop", suppressing the swipe-recovery path.
    if (!userToken) {
      MovexGeo.stopTracking().catch(() => {});
      return;
    }
    if (!userId) return;

    let cancelled = false;
    (async () => {
      try {
        if (permissionKeys.length) {
          const result = await checkMultiple(permissionKeys);
          const allGranted = permissionKeys.every((p) => isGranted(result[p]));
          if (!allGranted) return;
        }
        await MovexGeo.setUserId(String(userId));
        if (cancelled) return;
        await MovexGeo.startTracking("adaptive");
      } catch {
        // setUserId / start failed; user can retry from the gate's modal
      }
    })();

    return () => {
      cancelled = true;
      // No stopTracking() here — must outlive JS-context teardown.
    };
  }, [userId, userToken, permissionKeys]);

  return null;
};

export default TrackingBootstrap;
